import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, ArrowUpRight } from "lucide-react";
import { useApp } from "../context/AppContext";
import { api, chf, fileUrl, imgSrc, formatApiErrorDetail } from "../lib/api";
import SEO from "../components/SEO";
import Lightbox from "../components/Lightbox";

export default function CartPage() {
  const { cart, removeFromCart, updateQty, customer } = useApp();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState(null);
  const [email, setEmail] = useState("");
  const [addr, setAddr] = useState({ full_name: "", line1: "", postal_code: "", city: "", country: "Suisse" });
  const [loading, setLoading] = useState(false);
  const [previews, setPreviews] = useState({});
  const [zoomSrc, setZoomSrc] = useState(null);

  // Aperçu du rendu personnalisé (composite backend, sans créer de commande)
  const customSig = cart.map((i) => i.lineId + JSON.stringify(i.customization || "")).join("|");
  useEffect(() => {
    const hasCustom = cart.some((i) => i.customization?.sides && Object.keys(i.customization.sides).length);
    if (!hasCustom) { setPreviews({}); return; }
    const items = cart.map((i) => ({ product_id: i.productId, sides: i.customization?.sides || null }));
    let cancelled = false;
    api.post("/preview/composite", { items })
      .then((r) => {
        if (cancelled) return;
        const map = {};
        r.data.previews.forEach((p) => {
          const lineId = cart[p.item]?.lineId;
          if (!lineId) return;
          map[lineId] = [...(map[lineId] || []), p];
        });
        setPreviews(map);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [customSig]); // eslint-disable-line

  useEffect(() => {
    if (customer && customer.user_id) {
      if (customer.email) setEmail((e) => e || customer.email);
      if (customer.shipping_address) setAddr((a) => (a.line1 ? a : customer.shipping_address));
    }
  }, [customer]);

  const subtotal = cart.reduce((s, i) => s + i.unitPrice * i.quantity, 0);
  const afterDiscount = discount ? subtotal * (1 - discount.percent / 100) : subtotal;
  const [shipInfo, setShipInfo] = useState({ price: 0, grid: "lettre", total_g: 0, packaging_g: 0 });

  // Devis frais de port côté serveur (source unique : poids + emballage + épaisseur + format)
  useEffect(() => {
    if (cart.length === 0) { setShipInfo({ price: 0, grid: "lettre", total_g: 0, packaging_g: 0 }); return; }
    const t = setTimeout(() => {
      api.post("/shipping/quote", { items: cart.map((i) => ({ product_id: i.productId, quantity: i.quantity })) })
        .then((r) => setShipInfo(r.data))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [cart]);

  const ship = shipInfo.price || 0;
  const total = afterDiscount + ship;

  const applyCode = async () => {
    if (!code.trim()) return;
    try {
      const { data } = await api.get(`/discounts/validate/${code.trim()}`);
      setDiscount(data);
      toast.success(`Code ${data.code} appliqué (-${data.percent}%)`);
    } catch (e) {
      setDiscount(null);
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const checkout = async () => {
    if (cart.length === 0) return;
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    if (!emailOk) {
      toast.error("Veuillez saisir un e-mail valide pour recevoir la confirmation");
      return;
    }
    if (!addr.full_name.trim() || !addr.line1.trim() || !addr.postal_code.trim() || !addr.city.trim()) {
      toast.error("Veuillez remplir votre adresse de livraison complète");
      return;
    }
    setLoading(true);
    // Stripe Checkout cannot render inside an iframe. Open a top-level tab now,
    // within the click gesture (so it isn't popup-blocked), then point it at Stripe.
    const payWindow = window.open("", "_blank");
    try {
      const items = cart.map((i) => ({
        product_id: i.productId,
        quantity: i.quantity,
        size: i.size || null,
        customization: i.customization || null,
      }));
      const { data } = await api.post("/payments/checkout", {
        items,
        discount_code: discount?.code || null,
        origin_url: window.location.origin,
        customer_email: email.trim(),
        shipping_address: {
          full_name: addr.full_name.trim(),
          line1: addr.line1.trim(),
          postal_code: addr.postal_code.trim(),
          city: addr.city.trim(),
          country: addr.country.trim() || "Suisse",
        },
      });
      const url = data.checkout_url;
      if (payWindow && !payWindow.closed) {
        payWindow.location.href = url;
        setLoading(false);
        toast.success("Page de paiement ouverte dans un nouvel onglet");
        return;
      }
      // Popup blocked: try to break out of the iframe, else same-window redirect.
      try {
        if (window.top && window.top !== window.self) {
          window.top.location.href = url;
          return;
        }
      } catch (err) {
        /* cross-origin top access blocked — fall back below */
      }
      window.location.href = url;
    } catch (e) {
      if (payWindow && !payWindow.closed) payWindow.close();
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-[1400px] flex-col items-start justify-center px-5 md:px-10">
        <SEO title="Panier" path="/panier" noindex />
        <h1 className="font-serif text-5xl font-light tracking-tight text-ink md:text-7xl">Votre panier est vide.</h1>
        <p className="mt-4 text-ink2">Il attend votre première création.</p>
        <Link to="/catalogue" className="mt-8 bg-ink px-8 py-4 text-sm uppercase tracking-widest text-cream transition-colors hover:bg-terra">
          Explorer le catalogue
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] px-5 pt-20 pb-28 md:px-10">
      <SEO title="Panier" path="/panier" noindex />
      <h1 className="mb-12 font-serif text-5xl font-light tracking-tight text-ink md:text-7xl">Panier</h1>
      <div className="grid gap-12 lg:grid-cols-[1.6fr_1fr]">
        <div className="divide-y divide-line border-y border-line" data-testid="cart-items">
          {cart.map((i) => (
            <div key={i.lineId} className="flex gap-5 py-6" data-testid={`cart-item-${i.lineId}`}>
              <div className="h-28 w-24 shrink-0 overflow-hidden border border-line bg-sand">
                <img src={imgSrc(i.preview || i.image_url)} alt={i.name} className="h-full w-full object-cover" />
              </div>
              <div className="flex flex-1 flex-col justify-between">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-serif text-xl font-medium text-ink">{i.name}</h3>
                    {i.size && <p className="text-sm text-ink2">{i.sizeLabel || "Taille"} : {i.size}</p>}
                    {i.customization?.sides && Object.entries(i.customization.sides).map(([face, c]) => (
                      <p key={face} className="text-sm text-ink2">
                        {face} : {c.text?.value ? `texte « ${c.text.value} »` : ""}{c.text?.value && c.image ? " + " : ""}{c.image ? "image" : ""}
                      </p>
                    ))}
                    {previews[i.lineId]?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2" data-testid={`cart-preview-${i.lineId}`}>
                        {previews[i.lineId].map((p, k) => (
                          <button key={k} onClick={() => setZoomSrc(p.data)} data-testid={`cart-preview-thumb-${i.lineId}-${k}`}
                            title="Aperçu du rendu — cliquer pour agrandir"
                            className="relative h-20 w-20 overflow-hidden border border-line bg-white transition-transform hover:scale-105">
                            <img src={p.data} alt={`Aperçu ${p.side}`} className="h-full w-full object-cover" />
                            <span className="absolute inset-x-0 bottom-0 bg-ink/70 px-1 text-center text-[9px] uppercase tracking-wider text-cream">{p.side}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button onClick={() => removeFromCart(i.lineId)} data-testid={`remove-${i.lineId}`} className="text-ink3 transition-colors hover:text-terra">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center border border-line">
                    <button onClick={() => updateQty(i.lineId, i.quantity - 1)} data-testid={`qty-dec-${i.lineId}`} className="px-3 py-1 text-ink2 hover:bg-sand">−</button>
                    <span className="w-8 text-center text-sm" data-testid={`qty-${i.lineId}`}>{i.quantity}</span>
                    <button onClick={() => updateQty(i.lineId, i.quantity + 1)} data-testid={`qty-inc-${i.lineId}`} className="px-3 py-1 text-ink2 hover:bg-sand">+</button>
                  </div>
                  <span className="font-medium text-ink">{chf(i.unitPrice * i.quantity)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="h-fit border border-line bg-sand/50 p-8">
          <h2 className="mb-6 font-serif text-2xl font-medium text-ink">Récapitulatif</h2>
          <div className="mb-6 flex gap-2">
            <input
              value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code promo"
              data-testid="discount-input"
              className="flex-1 border border-line bg-cream px-4 py-3 text-sm outline-none focus:border-ink"
            />
            <button onClick={applyCode} data-testid="apply-discount" className="border border-ink px-5 text-xs uppercase tracking-widest text-ink transition-colors hover:bg-ink hover:text-cream">
              Appliquer
            </button>
          </div>
          <label className="mb-2 block text-xs uppercase tracking-widest text-ink3">E-mail (pour la confirmation) *</label>
          <input
            value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="vous@exemple.ch"
            data-testid="email-input"
            className="mb-4 w-full border border-line bg-cream px-4 py-3 text-sm outline-none focus:border-ink"
          />
          <label className="mb-2 block text-xs uppercase tracking-widest text-ink3">Adresse de livraison *</label>
          <div className="mb-6 space-y-2">
            <input value={addr.full_name} onChange={(e) => setAddr({ ...addr, full_name: e.target.value })} placeholder="Nom et prénom"
              data-testid="addr-name" className="w-full border border-line bg-cream px-4 py-3 text-sm outline-none focus:border-ink" />
            <input value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} placeholder="Rue et numéro"
              data-testid="addr-line1" className="w-full border border-line bg-cream px-4 py-3 text-sm outline-none focus:border-ink" />
            <div className="flex gap-2">
              <input value={addr.postal_code} onChange={(e) => setAddr({ ...addr, postal_code: e.target.value })} placeholder="NPA"
                data-testid="addr-postal" className="w-1/3 border border-line bg-cream px-4 py-3 text-sm outline-none focus:border-ink" />
              <input value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} placeholder="Ville"
                data-testid="addr-city" className="w-2/3 border border-line bg-cream px-4 py-3 text-sm outline-none focus:border-ink" />
            </div>
            <input value={addr.country} onChange={(e) => setAddr({ ...addr, country: e.target.value })} placeholder="Pays"
              data-testid="addr-country" className="w-full border border-line bg-cream px-4 py-3 text-sm outline-none focus:border-ink" />
          </div>
          <div className="space-y-2 border-t border-line pt-4 text-sm">
            <div className="flex justify-between text-ink2"><span>Sous-total</span><span>{chf(subtotal)}</span></div>
            {discount && (
              <div className="flex justify-between text-terra"><span>Réduction ({discount.code})</span><span>−{chf(subtotal - afterDiscount)}</span></div>
            )}
            <div className="flex justify-between text-ink2">
              <span data-testid="cart-shipping-format">
                Livraison{shipInfo.total_g ? ` (${shipInfo.total_g} g · ${shipInfo.grid === "colis" ? "colis" : "lettre"})` : ""}
              </span>
              <span data-testid="cart-shipping">{ship > 0 ? chf(ship) : "—"}</span>
            </div>
            <div className="flex justify-between border-t border-line pt-3 text-lg font-medium text-ink">
              <span>Total</span><span data-testid="cart-total">{chf(total)}</span>
            </div>
            <p className="pt-1 text-xs text-ink3">Frais de port selon le format (lettre ou colis), le poids et l'épaisseur, emballage inclus. Taxes calculées au paiement.</p>
          </div>
          <button
            onClick={checkout} disabled={loading} data-testid="checkout-btn"
            className="mt-6 flex w-full items-center justify-center gap-2 bg-ink py-4 text-sm uppercase tracking-widest text-cream transition-colors hover:bg-terra disabled:opacity-50"
          >
            {loading ? "Redirection…" : "Payer"} <ArrowUpRight className="h-4 w-4" />
          </button>
          <p className="mt-3 text-center text-xs text-ink3">Paiement sécurisé par Stripe</p>
        </div>
      </div>

      {zoomSrc && <Lightbox src={zoomSrc} alt="Aperçu du rendu personnalisé" onClose={() => setZoomSrc(null)} />}
    </div>
  );
}
